// The builder's document edits, gestures and undo history, kept free of React so they can be
// tested on their own. A "piece" is one constraint entry or one given; the builder state
// remembers which pieces the app added (everything else is the setter's own and locked).
import {
  pieceKey,
  type BuilderState,
  type ConstraintEntry,
  type PuzzleDoc,
  type Rc,
} from '@zinc/shared';
import type { Variant } from './palette';

export interface Work {
  title: string;
  doc: PuzzleDoc;
  builder: BuilderState;
}

export interface Timeline {
  past: Work[];
  present: Work;
  future: Work[];
}

const HISTORY = 200;

export function startTimeline(w: Work): Timeline {
  return { past: [], present: w, future: [] };
}

/** A new step: the present goes into the past, the future is dropped. */
export function commit(t: Timeline, next: Work): Timeline {
  if (next === t.present) return t;
  return { past: [...t.past, t.present].slice(-HISTORY), present: next, future: [] };
}

/** Changes the present without a step of its own (the rules text written from constraints). */
export function amend(t: Timeline, next: Work): Timeline {
  return { ...t, present: next };
}

export function undo(t: Timeline): Timeline {
  const prev = t.past.at(-1);
  if (!prev) return t;
  return { past: t.past.slice(0, -1), present: prev, future: [t.present, ...t.future] };
}

export function redo(t: Timeline): Timeline {
  const next = t.future[0];
  if (!next) return t;
  return { past: [...t.past, t.present], present: next, future: t.future.slice(1) };
}

export const same = (a: Rc, b: Rc) => a[0] === b[0] && a[1] === b[1];

function rcs(v: unknown): Rc[] {
  return Array.isArray(v)
    ? v.filter((x): x is Rc => Array.isArray(x) && x.length === 2).map((x) => [x[0], x[1]])
    : [];
}

/** Every cell an entry draws on: its cells, an arrow's circle, a clone's copy. */
export function cellsOf(e: ConstraintEntry): Rc[] {
  return [...rcs(e.circle), ...rcs(e.cells), ...rcs(e.copy)];
}

export function clueOf(e: ConstraintEntry): Rc | null {
  const c = e.clue;
  return Array.isArray(c) && c.length === 2 ? [c[0] as number, c[1] as number] : null;
}

const EDGE_TYPES = ['difference', 'ratio', 'edge-sum', 'greater-than'];
const GLOBAL_TYPES = ['anti-knight', 'anti-king', 'non-consecutive', 'disjoint-groups', 'diagonal'];

export function isEdge(e: ConstraintEntry): boolean {
  return EDGE_TYPES.includes(e.type) && e.negative !== true;
}

export function isGlobal(e: ConstraintEntry): boolean {
  return GLOBAL_TYPES.includes(e.type) || e.negative === true;
}

// ── Pieces ──────────────────────────────────────────────────────────────────

export function givenAt(doc: PuzzleDoc, rc: Rc): string | null {
  return doc.givens.find((g) => same(g.cell, rc))?.value ?? null;
}

/** Sets (or, with null, clears) the given on each cell. */
export function setGivens(doc: PuzzleDoc, cells: Rc[], value: string | null): PuzzleDoc {
  const rest = doc.givens.filter((g) => !cells.some((c) => same(c, g.cell)));
  const added = value === null ? [] : cells.map((cell) => ({ cell, value }));
  const givens = [...rest, ...added].sort((a, b) => a.cell[0] - b.cell[0] || a.cell[1] - b.cell[1]);
  return { ...doc, givens };
}

export function addEntry(doc: PuzzleDoc, e: ConstraintEntry): PuzzleDoc {
  return { ...doc, constraints: [...doc.constraints, e] };
}

export function removeEntries(doc: PuzzleDoc, indices: number[]): PuzzleDoc {
  return { ...doc, constraints: doc.constraints.filter((_, i) => !indices.includes(i)) };
}

export function updateEntry(doc: PuzzleDoc, index: number, e: ConstraintEntry): PuzzleDoc {
  return { ...doc, constraints: doc.constraints.map((x, i) => (i === index ? e : x)) };
}

/** Entries drawn on a cell (lines, cages, cell marks), newest first. */
export function entriesAtCell(doc: PuzzleDoc, rc: Rc): number[] {
  const out: number[] = [];
  doc.constraints.forEach((e, i) => {
    if (!isEdge(e) && !isGlobal(e) && cellsOf(e).some((c) => same(c, rc))) out.push(i);
  });
  return out.reverse();
}

export function edgeEntryAt(doc: PuzzleDoc, a: Rc, b: Rc): number {
  return doc.constraints.findIndex((e) => {
    if (!isEdge(e)) return false;
    const cells = cellsOf(e);
    return (
      cells.length === 2 &&
      ((same(cells[0]!, a) && same(cells[1]!, b)) || (same(cells[0]!, b) && same(cells[1]!, a)))
    );
  });
}

export function clueEntryAt(doc: PuzzleDoc, clue: Rc): number {
  return doc.constraints.findIndex((e) => {
    const c = clueOf(e);
    return c !== null && same(c, clue);
  });
}

export function globalIndex(
  doc: PuzzleDoc,
  type: string,
  params?: Record<string, unknown>,
): number {
  return doc.constraints.findIndex(
    (e) =>
      e.type === type &&
      e.negative !== true &&
      Object.entries(params ?? {}).every(([k, v]) => e[k] === v),
  );
}

/** Switches a rule for the whole grid on or off. */
export function setGlobal(
  doc: PuzzleDoc,
  type: string,
  params: Record<string, unknown> | undefined,
  on: boolean,
): PuzzleDoc {
  const i = globalIndex(doc, type, params);
  if (on && i < 0) return addEntry(doc, { type, ...(params ?? {}) });
  if (!on && i >= 0) return removeEntries(doc, [i]);
  return doc;
}

/** "All dots given" for a dot or edge type: its negative entry. */
export function negativeIndex(doc: PuzzleDoc, type: string): number {
  return doc.constraints.findIndex((e) => e.type === type && e.negative === true);
}

export function setNegative(
  doc: PuzzleDoc,
  type: string,
  values: number[],
  on: boolean,
): PuzzleDoc {
  const i = negativeIndex(doc, type);
  if (on && i < 0) return addEntry(doc, { type, negative: true, values });
  if (!on && i >= 0) return removeEntries(doc, [i]);
  return doc;
}

// ── Pointer geometry (fractional cell units: r = row, c = column) ────────────

export interface Pt {
  r: number;
  c: number;
}

export function cellOfPt(pt: Pt): Rc {
  return [Math.floor(pt.r), Math.floor(pt.c)];
}

const inside = (rc: Rc, rows: number, cols: number) =>
  rc[0] >= 0 && rc[1] >= 0 && rc[0] < rows && rc[1] < cols;

/** The edge nearest the point, between two cells of the grid. */
export function edgeAt(pt: Pt, rows: number, cols: number): [Rc, Rc] | null {
  const [R, C] = cellOfPt(pt);
  if (!inside([R, C], rows, cols)) return null;
  const fr = pt.r - R;
  const fc = pt.c - C;
  const options: { d: number; pair: [Rc, Rc] }[] = [
    {
      d: fc,
      pair: [
        [R, C - 1],
        [R, C],
      ],
    },
    {
      d: 1 - fc,
      pair: [
        [R, C],
        [R, C + 1],
      ],
    },
    {
      d: fr,
      pair: [
        [R - 1, C],
        [R, C],
      ],
    },
    {
      d: 1 - fr,
      pair: [
        [R, C],
        [R + 1, C],
      ],
    },
  ];
  const ok = options
    .filter((o) => inside(o.pair[0], rows, cols) && inside(o.pair[1], rows, cols))
    .sort((a, b) => a.d - b.d);
  return ok[0]?.pair ?? null;
}

/** The cells around the grid corner nearest the point (2 to 4 of them), for a quadruple. */
export function cornerAt(pt: Pt, rows: number, cols: number): Rc[] | null {
  const vr = Math.round(pt.r);
  const vc = Math.round(pt.c);
  if (vr < 0 || vc < 0 || vr > rows || vc > cols) return null;
  const around: Rc[] = [
    [vr - 1, vc - 1],
    [vr - 1, vc],
    [vr, vc - 1],
    [vr, vc],
  ];
  const cells = around.filter((rc) => inside(rc, rows, cols));
  return cells.length >= 2 ? cells : null;
}

/** The place just outside the grid at the point: a row's or column's end (`corners` too). */
export function outsideAt(pt: Pt, rows: number, cols: number, corners = false): Rc | null {
  const [R, C] = cellOfPt(pt);
  if (R < -1 || C < -1 || R > rows || C > cols) return null;
  const outR = R < 0 || R >= rows;
  const outC = C < 0 || C >= cols;
  if (!outR && !outC) return null;
  if (outR && outC && !corners) return null;
  return [R, C];
}

/** Diagonal directions from an outside place that enter the grid at once. */
export function diagonalsFrom(clue: Rc, rows: number, cols: number): [number, number][] {
  const dirs: [number, number][] = [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];
  return dirs.filter(([dr, dc]) => inside([clue[0] + dr, clue[1] + dc], rows, cols));
}

const adjacent = (a: Rc, b: Rc) => Math.max(Math.abs(a[0] - b[0]), Math.abs(a[1] - b[1])) === 1;

/** A path dragged through cells: steps to a neighbour (diagonals too), backs up over the
 * previous cell, ignores jumps and cells already on it. */
export function extendPath(path: Rc[], rc: Rc): Rc[] {
  const last = path.at(-1);
  if (!last) return [rc];
  if (same(last, rc)) return path;
  const prev = path.at(-2);
  if (prev && same(prev, rc)) return path.slice(0, -1);
  if (!adjacent(last, rc) || path.some((p) => same(p, rc))) return path;
  return [...path, rc];
}

/** Cells dragged over for a cage or an area: each once, in the order reached. */
export function extendArea(area: Rc[], rc: Rc): Rc[] {
  return area.some((p) => same(p, rc)) ? area : [...area, rc];
}

// ── Entries from gestures ───────────────────────────────────────────────────

const cellsJson = (cells: Rc[]) => cells.map((c) => [c[0], c[1]] as Rc);

export function lineEntry(v: Variant, path: Rc[]): ConstraintEntry | null {
  if (path.length < 2) return null;
  return { type: v.type, ...(v.params ?? {}), cells: cellsJson(path) };
}

export function arrowEntry(path: Rc[]): ConstraintEntry | null {
  if (path.length < 2) return null;
  return { type: 'arrow', circle: [path[0]!], cells: cellsJson(path.slice(1)) };
}

export function areaEntry(v: Variant, cells: Rc[]): ConstraintEntry | null {
  if (cells.length === 0) return null;
  const sorted = [...cells].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return { type: v.type, ...(v.params ?? {}), cells: cellsJson(sorted) };
}

export function edgeEntry(v: Variant, pair: [Rc, Rc]): ConstraintEntry {
  return { type: v.type, ...(v.params ?? {}), cells: cellsJson(pair) };
}

export function outsideEntry(v: Variant, clue: Rc, rows: number, cols: number): ConstraintEntry {
  if (v.type === 'little-killer') {
    const direction = diagonalsFrom(clue, rows, cols)[0] ?? [1, 1];
    return { type: v.type, ...(v.params ?? {}), clue, direction };
  }
  return { type: v.type, ...(v.params ?? {}), clue };
}

export function squareEntry(top: Rc, rows: number, cols: number): ConstraintEntry | null {
  const cells: Rc[] = [];
  for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) cells.push([top[0] + r, top[1] + c]);
  if (!cells.every((rc) => inside(rc, rows, cols))) return null;
  return { type: 'magic-square', cells };
}

export function cloneEntry(a: Rc[], b: Rc[]): ConstraintEntry | null {
  if (a.length === 0 || a.length !== b.length) return null;
  return { type: 'clone', cells: cellsJson(a), copy: cellsJson(b) };
}

/** Taps a cell mark (odd, even, minimum…): on when the cell has none of that type, off if it
 * has. One entry per cell, as the generator makes them. */
export function toggleCellMark(doc: PuzzleDoc, v: Variant, rc: Rc): PuzzleDoc {
  const i = doc.constraints.findIndex(
    (e) => e.type === v.type && cellsOf(e).length === 1 && same(cellsOf(e)[0]!, rc),
  );
  if (i >= 0) return removeEntries(doc, [i]);
  // A cell has one mark: odd replaces even and the other way round.
  const others = doc.constraints
    .map((e, k) => ({ e, k }))
    .filter(
      ({ e }) =>
        ['odd', 'even', 'minimum', 'maximum', 'fortress'].includes(e.type) &&
        cellsOf(e).length === 1 &&
        same(cellsOf(e)[0]!, rc),
    )
    .map(({ k }) => k);
  return addEntry(removeEntries(doc, others), { type: v.type, ...(v.params ?? {}), cells: [rc] });
}

// ── Regions ─────────────────────────────────────────────────────────────────

/** The grid's regions as cell lists: jigsaw regions, or the boxes. */
export function regionsOf(doc: PuzzleDoc): Rc[][] {
  if (doc.grid.regions) return doc.grid.regions;
  const { rows, cols, boxRows: br, boxCols: bc } = doc.grid;
  if (!br || !bc) return [];
  const out: Rc[][] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const k = Math.floor(r / br) * Math.floor(cols / bc) + Math.floor(c / bc);
      (out[k] ??= []).push([r, c]);
    }
  }
  return out;
}

export function regionIndexAt(doc: PuzzleDoc, rc: Rc): number {
  return regionsOf(doc).findIndex((cells) => cells.some((c) => same(c, rc)));
}

/** Moves a cell into region `k` (the grid becomes a jigsaw if it had boxes). */
export function paintRegion(doc: PuzzleDoc, rc: Rc, k: number): PuzzleDoc {
  const regions = regionsOf(doc).map((cells) => cells.filter((c) => !same(c, rc)));
  while (regions.length <= k) regions.push([]);
  regions[k] = [...regions[k]!, rc].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  return {
    ...doc,
    grid: { ...doc.grid, regions: regions.filter((r) => r.length > 0), boxRows: 0, boxCols: 0 },
  };
}

/** What's wrong with the regions, if anything: each needs as many cells as there are digits. */
export function regionProblem(doc: PuzzleDoc): string | null {
  if (!doc.grid.regions) return null;
  const n = doc.grid.digits.length;
  const off = doc.grid.regions.filter((r) => r.length !== n).length;
  if (doc.grid.regions.length !== n || off > 0) return `Each of the ${n} regions needs ${n} cells.`;
  return null;
}

// ── App-added pieces ────────────────────────────────────────────────────────

export function entryKey(e: ConstraintEntry): string {
  return pieceKey({ entry: e });
}

export function givenKey(rc: Rc): string {
  return pieceKey({ given: rc });
}

function presentKeys(doc: PuzzleDoc): Set<string> {
  return new Set([...doc.constraints.map(entryKey), ...doc.givens.map((g) => givenKey(g.cell))]);
}

/** The builder state with only the added pieces still in the document. */
export function prune(builder: BuilderState, doc: PuzzleDoc): BuilderState {
  const keys = presentKeys(doc);
  const added = builder.added.filter((k) => keys.has(k));
  return added.length === builder.added.length ? builder : { ...builder, added };
}

/** The setter's own pieces: everything the app didn't add. */
export function lockedCount(doc: PuzzleDoc, builder: BuilderState): number {
  const added = new Set(builder.added);
  return [...presentKeys(doc)].filter((k) => !added.has(k)).length;
}

/** The pieces Tidy may remove: the ones the app added. */
export function removablePieces(
  doc: PuzzleDoc,
  builder: BuilderState,
): { constraints: number[]; givens: Rc[] } {
  const added = new Set(builder.added);
  return {
    constraints: doc.constraints.flatMap((e, i) => (added.has(entryKey(e)) ? [i] : [])),
    givens: doc.givens.filter((g) => added.has(givenKey(g.cell))).map((g) => g.cell),
  };
}

/** Every piece of a document, as added by the app (a generated candidate). */
export function allKeys(doc: PuzzleDoc): string[] {
  return [...presentKeys(doc)];
}

/** A setter's edit: the puzzle is no longer the generator's own. */
export function byHand(w: Work, doc: PuzzleDoc): Work {
  return { ...w, doc, builder: { ...w.builder, generated: false } };
}

/** The app adds pieces (accepted suggestions, Complete): they stay unlocked. */
export function byApp(w: Work, doc: PuzzleDoc, keys: string[]): Work {
  const added = [...new Set([...w.builder.added, ...keys])];
  return { ...w, doc, builder: { ...w.builder, added, generated: false } };
}

// ── Names ───────────────────────────────────────────────────────────────────

const cellName = (rc: Rc) => `r${rc[0] + 1}c${rc[1] + 1}`;

function outsidePlace(clue: Rc, rows: number, cols: number): string {
  const [r, c] = clue;
  if (r < 0 && c < 0) return 'the top-left corner';
  if (r < 0 && c >= cols) return 'the top-right corner';
  if (r >= rows && c < 0) return 'the bottom-left corner';
  if (r >= rows && c >= cols) return 'the bottom-right corner';
  if (r < 0) return `above column ${c + 1}`;
  if (r >= rows) return `below column ${c + 1}`;
  if (c < 0) return `left of row ${r + 1}`;
  return `right of row ${r + 1}`;
}

const NAMES: Record<string, string> = {
  'extra-region': 'Extra region',
  diagonal: 'Diagonal',
  'disjoint-groups': 'Disjoint groups',
  odd: 'Odd cell',
  even: 'Even cell',
  minimum: 'Minimum',
  maximum: 'Maximum',
  fortress: 'Fortress cell',
  thermo: 'Thermo',
  'slow-thermo': 'Slow thermo',
  arrow: 'Arrow',
  'double-arrow': 'Double arrow',
  renban: 'Renban',
  nabner: 'Nabner',
  palindrome: 'Palindrome',
  'region-sum': 'Region sum line',
  between: 'Between line',
  lockout: 'Lockout line',
  entropic: 'Entropic line',
  modular: 'Modular line',
  parity: 'Parity line',
  zipper: 'Zipper line',
  'little-killer': 'Little killer',
  sandwich: 'Sandwich',
  'x-sum': 'X-sum',
  skyscraper: 'Skyscraper',
  'frame-sum': 'Frame sum',
  battlefield: 'Battlefield',
  quadruple: 'Quadruple',
  'greater-than': 'Greater-than sign',
  'anti-knight': 'Anti-knight',
  'anti-king': 'Anti-king',
  'non-consecutive': 'Non-consecutive',
  clone: 'Clone',
  'magic-square': 'Magic square',
};

function num(e: ConstraintEntry, k: string): number | null {
  const v = e[k];
  return typeof v === 'number' ? v : null;
}

/** A constraint's name as a setter says it ("Black kropki dot", "Killer cage 14"). */
export function entryName(e: ConstraintEntry): string {
  switch (e.type) {
    case 'difference':
      return (num(e, 'value') ?? 1) === 1
        ? 'White kropki dot'
        : `Difference dot ${num(e, 'value')}`;
    case 'ratio':
      return (num(e, 'value') ?? 2) === 2 ? 'Black kropki dot' : `Ratio dot ${num(e, 'value')}`;
    case 'edge-sum': {
      const v = num(e, 'value');
      return v === 10 ? 'X' : v === 5 ? 'V' : v !== null ? `Sum ${v}` : 'Sum clue';
    }
    case 'whispers': {
      const d = num(e, 'difference') ?? 5;
      return d === 5 ? 'German whispers' : d === 4 ? 'Dutch whispers' : `Whispers ${d}`;
    }
    case 'killer':
      return num(e, 'sum') !== null ? `Killer cage ${num(e, 'sum')}` : 'Killer cage';
    case 'little-killer':
    case 'sandwich':
    case 'x-sum':
    case 'frame-sum':
    case 'battlefield':
      return `${NAMES[e.type]} ${num(e, 'sum') ?? ''}`.trim();
    case 'skyscraper':
      return `Skyscraper ${num(e, 'count') ?? ''}`.trim();
    case 'quadruple':
      return `Quadruple ${Array.isArray(e.digits) ? e.digits.join(' ') : ''}`.trim();
    case 'diagonal':
      return e.direction === 'negative' ? 'Diagonal \\' : 'Diagonal /';
    default:
      return NAMES[e.type] ?? e.type;
  }
}

/** "Black kropki dot, r8c4–r8c5", "Sandwich 12 above column 3". */
export function describeEntry(e: ConstraintEntry, rows: number, cols: number): string {
  const name = entryName(e);
  if (e.negative === true) return `${name}: all given`;
  const clue = clueOf(e);
  if (clue) return `${name} ${outsidePlace(clue, rows, cols)}`;
  const cells = cellsOf(e);
  if (cells.length === 0) return name;
  if (cells.length === 1) return `${name}, ${cellName(cells[0]!)}`;
  return `${name}, ${cellName(cells[0]!)}–${cellName(cells.at(-1)!)}`;
}
